import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("polling");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("missing"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch {}
      if (attempts.current < 15) {
        setTimeout(poll, 2000);
      } else {
        setStatus("timeout");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="app-shell grain flex flex-col p-6 min-h-[100dvh]">
      <div className="flex-1 flex flex-col justify-center animate-fade-up">
        {status === "polling" && (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
            <h1 className="mt-6 font-serif text-4xl">Confirming your payment…</h1>
            <p className="mt-2 text-muted-foreground">This usually takes a couple of seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-10 w-10 text-primary" strokeWidth={1.5} />
            <h1 className="mt-6 font-serif text-5xl">You're in.</h1>
            <p className="mt-3 text-muted-foreground max-w-md">
              Premium is active. A second exchange partner is waiting for you tomorrow — or right now, if the pool is deep.
            </p>
            <Button
              onClick={() => nav("/today")}
              className="mt-10 rounded-full h-14 px-8 bg-primary hover:bg-primary/90 self-start"
              data-testid="paid-continue"
            >
              Back to Today
            </Button>
          </>
        )}
        {(status === "failed" || status === "timeout" || status === "missing") && (
          <>
            <h1 className="font-serif text-4xl">Payment not confirmed.</h1>
            <p className="mt-3 text-muted-foreground">
              We couldn't confirm your payment. If money was taken it'll be back within a few minutes.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/premium">
                <Button variant="outline" className="rounded-full h-12" data-testid="paid-retry">Try again</Button>
              </Link>
              <Link to="/today">
                <Button className="rounded-full h-12 bg-primary hover:bg-primary/90" data-testid="paid-home">Back to Today</Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
